import React, { FC, useEffect, useState } from 'react';
import {
    Button,
    DialogActions,
    FormControl,
    FormControlLabel,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    Switch,
    TextField,
} from '@mui/material';
import { useFormik } from 'formik';
import { AuthMethod, Role, Technician } from 'models/user';
import AuthMethodFields, {
    authMethodVariables,
    validateAuthMethodFields,
} from 'components/users/shared/auth-method-fields/auth-method-fields.component';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@apollo/client';
import { getRolesTemplate } from 'apollo/templates/user/query.graphql';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { modifyTechnicianTemplate } from 'apollo/templates/user/mutation.graphql';
import { userIdVar } from 'apollo/reactive-vars';
import { SalesforceUser } from 'models/organization';
import { currentOrgVar } from 'apollo/reactive-variables/organization';
import { customerSettings } from 'utils/customerSettings';
import { CONSTANTS } from 'config/constants';
import { salesforceUsersTemplate } from 'apollo/templates/organization';
import { nameRegExp } from 'config/regex';

type UpdateTechnicianFormProps = {
    onUpdateTechnicianClick: () => void;
    onDialogCloseClick: () => void;
    technician: Technician;
};

const UpdateTechnicianForm: FC<UpdateTechnicianFormProps> = ({
    onUpdateTechnicianClick,
    onDialogCloseClick,
    technician,
}: UpdateTechnicianFormProps) => {
    // Users created before DD-3552 carry no method on older cached rows; the backend defaults them to password.
    const currentMethod = technician.authMethod ?? AuthMethod.PASSWORD;
    const { showBoundary } = useDenthubErrorBoundary();
    const { t: translate } = useTranslation(['common', 'users', 'organization']);
    const [isFirstNameValid, setIsFirstNameValid] = useState<boolean>(false);
    const [isLastNameValid, setIsLastNameValid] = useState<boolean>(false);
    const currentOrganization = currentOrgVar();

    const isSalesForceEnabled = customerSettings.getCustomerSetting(
        currentOrganization.customerSettings,
        CONSTANTS.SETTINGS_NAMES.SALESFORCE_INTEGRATION,
    );

    const { data: { roles } = {}, loading: isRolesLoading } = useQuery(getRolesTemplate, {
        notifyOnNetworkStatusChange: true,
        variables: { type: 'TECHNICIAN' },
        onError(error) {
            showBoundary(error);
        },
    });

    const { data: { salesforceUsers } = {}, loading: isSalesforceUsersLoading } = useQuery(salesforceUsersTemplate, {
        onError(error) {
            showBoundary(error);
        },
    });

    const [modifyTechnician] = useMutation(modifyTechnicianTemplate, {
        onCompleted: () => {
            onUpdateTechnicianClick();
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const validate = (values: Technician) => {
        const errors: Record<string, string> = {};
        if (!values.firstName) {
            errors.firstName = translate('users:VALIDATION:FIRST_NAME');
        }
        if (values.firstName && !isFirstNameValid) {
            errors.firstName = translate('common:VALIDATION.INCORRECT_NAME');
        }
        if (!values.lastName) {
            errors.lastName = translate('users:VALIDATION:LAST_NAME');
        }
        if (values.lastName && !isLastNameValid) {
            errors.lastName = translate('common:VALIDATION.INCORRECT_NAME');
        }
        if (!values.email) {
            errors.email = translate('users:VALIDATION:EMAIL');
        }
        if (!values.role) {
            errors.role = translate('users:VALIDATION:ROLE');
        }
        validateAuthMethodFields(values, translate, errors, { currentMethod });

        return errors;
    };
    const formik = useFormik({
        initialValues: {
            firstName: technician.firstName,
            lastName: technician.lastName,
            role: technician.role,
            email: technician.email,
            active: technician.active,
            isShared: technician.isShared || false,
            organization: technician.organization,
            salesforceUser: technician.salesforceUser,
            authMethod: currentMethod,
            syncid: technician.syncid ?? '',
            enableUserSync: technician.enableUserSync || false,
            personalIdentityNumber: technician.personalIdentityNumber ?? '',
        },
        validate,
        enableReinitialize: true,
        onSubmit: async (values: Technician) => {
            const technicianVal = {
                _id: technician._id,
                name: {
                    firstName: values.firstName,
                    lastName: values.lastName,
                },
                email: values.email,
                role: values.role,
                active: values.active,
                isShared: values.isShared,
                organization: null as unknown,
                salesforceUser: values.salesforceUser,
                ...authMethodVariables(values),
            };
            await modifyTechnician({ variables: { technician: technicianVal } });
            onDialogCloseClick();
        },
    });

    useEffect(() => {
        if (technician) {
            setIsFirstNameValid(nameRegExp.test(technician.firstName || ''));
            setIsLastNameValid(nameRegExp.test(technician.lastName || ''));
        }
    }, [technician]);

    const handleFirstNameChange = (newFirstName: string) => {
        const firstName = newFirstName.charAt(0).toUpperCase() + newFirstName.slice(1);
        setIsFirstNameValid(nameRegExp.test(firstName));
        formik.setFieldValue('firstName', firstName);
    };

    const handleLastNameChange = (newLastName: string) => {
        const lastName = newLastName.charAt(0).toUpperCase() + newLastName.slice(1);
        setIsLastNameValid(nameRegExp.test(lastName));
        formik.setFieldValue('lastName', lastName);
    };

    return (
        <form onSubmit={formik.handleSubmit}>
            <Grid container spacing={1} mb={2}>
                <Grid item xs={4}>
                    <FormControl fullWidth margin="dense">
                        <TextField
                            fullWidth
                            id="firstName"
                            label={translate('common:FIRST_NAME')}
                            helperText={formik.touched.firstName && formik.errors.firstName}
                            error={formik.touched.firstName && Boolean(formik.errors.firstName)}
                            onChange={(e) => handleFirstNameChange(e.target.value)}
                            onBlur={formik.getFieldProps('firstName').onBlur}
                            name={formik.getFieldProps('firstName').name}
                            value={formik.getFieldProps('firstName').value}
                        />
                    </FormControl>
                </Grid>
                <Grid item xs={4}>
                    <FormControl fullWidth margin="dense">
                        <TextField
                            fullWidth
                            id="lastName"
                            label={translate('common:LAST_NAME')}
                            helperText={formik.touched.lastName && formik.errors.lastName}
                            error={formik.touched.lastName && Boolean(formik.errors.lastName)}
                            onChange={(e) => handleLastNameChange(e.target.value)}
                            onBlur={formik.getFieldProps('lastName').onBlur}
                            name={formik.getFieldProps('lastName').name}
                            value={formik.getFieldProps('lastName').value}
                        />
                    </FormControl>
                </Grid>
                <Grid item xs={4}>
                    <FormControl fullWidth margin="dense">
                        <TextField
                            id="email"
                            label={translate('common:EMAIL')}
                            type="email"
                            disabled
                            helperText={formik.touched.email && formik.errors.email}
                            error={formik.touched.email && Boolean(formik.errors.email)}
                            {...formik.getFieldProps('email')}
                        />
                    </FormControl>
                </Grid>
                <Grid item xs={4}>
                    {!isRolesLoading && (
                        <FormControl margin="dense" fullWidth>
                            <InputLabel id="select-role">{translate('common:ROLE')}</InputLabel>
                            <Select
                                id="select-role"
                                label={translate('common:ROLE')}
                                error={formik.touched.role && Boolean(formik.errors.role)}
                                {...formik.getFieldProps('role')}
                            >
                                {roles.map((role: Role) => (
                                    <MenuItem key={role.role} value={role.role}>
                                        {translate(role.role)}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}
                </Grid>
                {isSalesForceEnabled ? (
                    <Grid item xs={4}>
                        {!isSalesforceUsersLoading && (
                            <FormControl margin="dense" fullWidth>
                                <InputLabel id="select-sf-user">{translate('organization:SALESFORCE_USER')}</InputLabel>
                                <Select
                                    id="select-sf-user"
                                    label={translate('organization:SALESFORCE_USER')}
                                    value={formik.values.salesforceUser?.id || ''}
                                    onChange={(event) => {
                                        formik.setFieldValue(
                                            'salesforceUser',
                                            salesforceUsers.find(
                                                (user: SalesforceUser) => user.id === event.target.value,
                                            ),
                                        );
                                    }}
                                >
                                    {salesforceUsers.map((sfUser: SalesforceUser) => (
                                        <MenuItem key={sfUser.id} value={sfUser.id}>
                                            {sfUser.name} - {sfUser.email}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}
                    </Grid>
                ) : undefined}
                {(technician.linkedOrganizations?.length || 0) <= 1 ? (
                    <Grid item xs={4} alignContent="center">
                        <FormControlLabel
                            id="isShared"
                            name="isShared"
                            control={
                                <Switch
                                    color="secondary"
                                    checked={formik.values.isShared}
                                    onChange={formik.handleChange}
                                />
                            }
                            label={translate('common:SHARED_USER')}
                        />
                    </Grid>
                ) : undefined}
                <Grid item xs={4} alignContent="center">
                    <FormControlLabel
                        id="active"
                        name="active"
                        control={
                            <Switch color="secondary" checked={formik.values.active} onChange={formik.handleChange} />
                        }
                        label={formik.values.active ? translate('common:ACTIVE') : translate('common:INACTIVE')}
                    />
                </Grid>
                <AuthMethodFields formik={formik} currentMethod={currentMethod} />
            </Grid>

            <DialogActions>
                <Button variant="contained" type="submit" disabled={!formik.isValid || userIdVar() === technician._id}>
                    {translate('common:SAVE')}
                </Button>
            </DialogActions>
        </form>
    );
};

export default UpdateTechnicianForm;
